<script lang="ts" setup>
import CurrentLanguage from '@/components/CurrentLanguage.vue';
import MediaList from '@/components/media/MediaList.vue';
import {
  Co2QuizQuestion,
  QuestionType,
  titleRules,
  questionTypeItems,
  calculationTypeItems,
  calculationLabelItems,
  CalculationType,
} from '@/models/co2-quiz.model';
import RichTextField from '@/components/common/RichTextField.vue';
import { useEvents, Event } from '@/services/events.composable';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import {
  Co2QuizStoreActions,
  Co2QuizStoreState,
} from '@/stores/co2-quiz.store';
import Co2QuizAnswersField from '@/components/co2quiz/Co2QuizAnswersField.vue';
import Co2QuizPartQuestionsField from '@/components/co2quiz/Co2QuizPartQuestionsField.vue';
import CategoriesField from '@/components/categories/CategoriesField.vue';
import LinksField from '@/components/common/LinksField.vue';
import { LabelsActions, LabelsState } from '@/stores/labels.store';
import { UserLabel } from '@/models/label.model';
import Co2CalculationWarnings from '@/components/co2quiz/Co2CalculationWarnings.vue';

const store = useStore<
  ShiftCMSState & { co2: Co2QuizStoreState; ls: LabelsState }
>();
const questions = computed<Co2QuizQuestion[]>(() => store.state.co2.questions);
const labels = computed<UserLabel[]>(() => store.state.ls.labels);

const events = useEvents();

const props = defineProps({
  data: {
    type: Object as PropType<Co2QuizQuestion>,
  },
});

const valid = ref<boolean>(false);

const question = ref<Co2QuizQuestion>({
  id: NEW_ID,
  title: '',
  hint: '',
  index: questions.value.length,
  unitValue: 0,
  averageValue: 0,
  isQuickScan: false,
  isDismissable: false,
  isValue: false,
  questionType: QuestionType.Answers,
  calculationType: CalculationType.Value,
  calculationLabels: [],
  answers: [],
  categories: [],
  relatedQuestions: [],
  media: [],
  description: '',
  sources: [],
  ...(props.data || {}),
});

const action = computed<string>(() =>
  question.value.id === NEW_ID ? 'Add' : 'Save',
);

const questionOptions = [
  ...questions.value.map((q) => ({ title: q.title, value: q })),
];

const saveCo2QuizQuestion = async () => {
  let success: boolean = false;

  if (valid.value && question.value.categories.length !== 0) {
    if (question.value.id === NEW_ID) {
      success = await store.dispatch(
        Co2QuizStoreActions.AddCo2QuizQuestion,
        question.value,
      );
    } else {
      success = await store.dispatch(
        Co2QuizStoreActions.UpdateCo2QuizQuestion,
        question.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved question',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(Co2QuizStoreActions.GetCo2Questions);
  }
};

const updateLanguage = async () => {
  if (question.value.id === NEW_ID) {
    Object.assign(question.value, { title: '', type: 'question' });
  } else {
    const q = await store.dispatch(
      Co2QuizStoreActions.GetCo2Question,
      question.value.id,
    );

    if (q) {
      Object.assign(question.value, { ...q });
    }
  }
};

const deleteQuestion = async () => {
  if (
    question.value &&
    confirm('Are you absolutely sure you want to delete this question?')
  ) {
    store.dispatch(Co2QuizStoreActions.DeleteQuestion, question.value.id);

    store.commit(Mutations.HideDrawer);
  }
};

onMounted(() => {
  events.on(Event.LanguageChanged, updateLanguage);
  store.dispatch(LabelsActions.GetLabels);
  if (props.data) {
    Object.assign(question.value, props.data);
  }
});

onBeforeUnmount(() => {
  events.off(Event.LanguageChanged, updateLanguage);
});
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveCo2QuizQuestion">
    <v-container>
      <h2>{{ $t(action) }} a question <CurrentLanguage /></h2>

      <v-text-field
        v-model="question.title"
        :rules="titleRules"
        label="Title"
        required
      ></v-text-field>

      <RichTextField
        label="Hint"
        :height="200"
        v-model="question.hint"
        :media="question.media"
      />

      <v-row>
        <v-col>
          <v-checkbox
            label="Quickscan Question"
            v-model="question.isQuickScan"
          />
        </v-col>
        <v-col>
          <v-checkbox label="Show as Co2 Value" v-model="question.isValue" />
        </v-col>
        <v-col>
          <v-text-field
            v-model.number="question.averageValue"
            label="Average value"
            class="calculation-field"
            required
            suffix="Kg CO₂"
            hide-details="auto"
            :style="{ background: 'white' }"
            hint="This value is used as the value on the home screen"
          ></v-text-field>
        </v-col>
      </v-row>

      <div class="calculation">
        <h4>
          Calculation
          {{ question.parentQuestion ? '(question)' : '(main question)' }}
        </h4>
        <v-row>
          <v-col>
            <v-select
              v-model="question.questionType"
              label="Question Type"
              class="calculation-field"
              :items="questionTypeItems"
              hide-details="auto"
              :style="{ background: 'white' }"
              required
            ></v-select>
          </v-col>
          <v-col>
            <v-text-field
              v-model.number="question.unitValue"
              label="Unit value"
              class="calculation-field"
              required
              suffix="Kg CO₂"
              hide-details="auto"
              :style="{ background: 'white' }"
              hint="This value is used as the basis for the Co2 calculation"
            ></v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="question.calculationType"
              label="Calculation Type"
              class="calculation-field"
              hide-details="auto"
              :style="{ background: 'white' }"
              :items="calculationTypeItems"
            ></v-select>
          </v-col>
        </v-row>
        <Co2CalculationWarnings :question="question" :questions="questions" />
        <v-select
          v-model="question.calculationLabels"
          label="Calculation Labels"
          class="calculation-field"
          chips
          multiple
          clearable
          hide-details="auto"
          :style="{ background: 'white' }"
          :items="calculationLabelItems"
        >
          <template v-slot:item="{ props }">
            <v-list-item v-bind="props">
              <v-list-item-title></v-list-item-title>
            </v-list-item> </template
        ></v-select>
      </div>
      <CategoriesField v-model="question.categories" :max="1" required />
      <Co2QuizAnswersField
        v-model="question.answers"
        :questions="questions"
        :labels="labels"
        v-if="question.questionType !== QuestionType.PartsQuestion"
      >
        <v-checkbox
          label="Show dismissable option"
          v-model="question.isDismissable"
          hide-details="auto"
          density="compact"
          class="ml-8"
        />
      </Co2QuizAnswersField>

      <h3>Navigation</h3>
      <v-select
        v-model="question.chainQuestion"
        label="Chain question"
        class="mb-8"
        hide-details="auto"
        clearable
        :style="{ background: 'white' }"
        :items="questionOptions"
        ><template v-slot:item="{ props }">
          <v-list-item v-bind="props">
            <v-list-item-title></v-list-item-title>
          </v-list-item> </template
      ></v-select>
      <v-select
        v-model="question.relatedQuestions"
        label="Related questions"
        multiple
        clearable
        class="mt-8 mb-8"
        hide-details="auto"
        :style="{ background: 'white' }"
        :items="questionOptions"
        ><template v-slot:item="{ props }">
          <v-list-item v-bind="props">
            <v-list-item-title></v-list-item-title>
          </v-list-item> </template
      ></v-select>
      <Co2QuizPartQuestionsField
        v-model="question"
        :questions="questions"
        v-if="question.questionType === QuestionType.PartsQuestion"
      />

      <h3>Read more</h3>
      <RichTextField
        label="Description"
        :height="200"
        v-model="question.description"
        :media="question.media"
      />
      <LinksField v-model="question.sources" label="Source" />

      <MediaList v-model="question" />
      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn
            class="mt-2"
            type="button"
            color="warning"
            block
            @click="deleteQuestion"
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
.calculation {
  background-color: rgb(179, 227, 255);
  padding: 16px;
  border-radius: 16px;
  margin: 16px 0;
  box-shadow: 2px 2px 4px silver;
}
.calculation-field {
  margin-bottom: 16px;
}
</style>
